import type { Nullable } from '@/types/utils';
import type { LocalStorageKey, LocalStorageSchema } from './schema';

function safeParse<T>(raw: Nullable<string>): Nullable<T> {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function safeStringify(value: unknown): Nullable<string> {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}

export function getItem<K extends LocalStorageKey>(
  key: K
): Nullable<LocalStorageSchema[K]> {
  try {
    return safeParse<LocalStorageSchema[K]>(window.localStorage.getItem(key));
  } catch {
    // Storage blocked (SecurityError): read as unset, like a missing key.
    return null;
  }
}

export function setItem<K extends LocalStorageKey>(
  key: K,
  value: LocalStorageSchema[K]
): void {
  const str = safeStringify(value);
  if (str !== null) {
    try {
      window.localStorage.setItem(key, str);
    } catch {
      // Swallow quota/SecurityError (e.g. Safari private mode) — caller treats writes as best-effort.
    }
  }
}

export function removeItem(key: LocalStorageKey): void {
  window.localStorage.removeItem(key);
}

export function clear(): void {
  window.localStorage.clear();
}

export const safeLocalStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
  get length() {
    return window.localStorage.length;
  },
};
